import { Goal } from 'modules/goals/types';
import { maxBy } from 'lodash';
import type { CampaignForMetric } from '../hooks/useQueryCampaignsForMetrics';
import type { TopCampaign } from '../hooks/useQueryGoalTopCampaign';
import type { QueryCampaignMetricsResponse } from '../hooks/useQueryCampaignMetrics';

/**
 * Calculates the top campaign for each system event goal based on their metrics
 */
export const getTopCampaignsBySystemEventGoal = (
  goals: Goal[],
  goalMetrics: QueryCampaignMetricsResponse | undefined,
  campaigns: CampaignForMetric[],
): { [goalId: string]: TopCampaign } => {
  if (!goalMetrics) {
    return {};
  }

  const result = goals.reduce((acc: { [goalId: string]: TopCampaign }, goal: Goal) => {
    const metric = goal.metricDefinition?.metric;

    // If the system event contains no mapping to a metric, skip it
    if (!metric || !goalMetrics?.campaignLevelMetrics) {
      return acc;
    }

    // Check if any campaign has this metric with a numeric value
    const hasValidMetric = goalMetrics.campaignLevelMetrics.some(
      (campaignMetric) => typeof campaignMetric.mediumMetrics[metric]?.value === 'number',
    );

    if (!hasValidMetric) {
      return acc;
    }

    const topCampaign = maxBy(goalMetrics.campaignLevelMetrics, (campaignMetric) => {
      const metricValue = campaignMetric.mediumMetrics[metric]?.value;
      // Return null for non-numeric values to ensure they're not selected
      return typeof metricValue === 'number' ? metricValue : null;
    });

    // If no campaign had a valid numeric value, skip this goal
    if (!topCampaign) {
      return acc;
    }

    const foundCampaign = campaigns.find(
      (campaign) => campaign.campaignId === topCampaign?.campaignId,
    );

    if (foundCampaign) {
      acc[goal.id] = {
        bestCampaign: {
          campaignId: foundCampaign.campaignId,
          campaignName: foundCampaign.campaignName,
        },
        count: goalMetrics.campaignLevelMetrics.length,
      };
    }

    return acc;
  }, {});

  return result;
};
